import ContentSection from "../ContentSection";
import { useTranslation } from "../../../state/i18n";
import ThemeSelect from "../../../components/selectors/ThemeSelect";
import FormSettingsService from "../../../services/FormSettingsService";
import { Toggle } from "../../../components/toggle/Toggle";
import { Show } from "solid-js";
import { Checkbox } from "../../../components/checkbox/Checkbox";
import FieldFilterSelection from "./FieldFilterSelection";
import { toFieldOption, toFieldValueOptions } from "../../../utils/selectOptionsUtil";
import FormServicesFactory from "../../../services/FormServicesFactory";
import MixedInput from "../../../components/inputs/MixedInput";
import { SEARCHABLE_FIELDS } from "../../../utils/fieldUtil";
import withErrorBoundary from "../../../utils/withErrorBoundary";
import { parseStringTemplate } from "../../../utils/stringTemplateUtil";
import { SEARCHABLE_VARIABLES, toVariableValueOption } from "../../../variables/variableUtil";
import { createDynamicValueOptions } from "../../../utils/menu/searchableMenuUtil";
import { MultiSelectWithSearchInput } from "../../../components/menu/MultiSelectWithSearch";
import FieldId from "../../../fields/FieldId";
import type { SelectOption } from "../../../types/Menu";
import { SingleSelectInput } from "../../../components/menu/Select";
import { isNil } from "lodash-es";
import SeparatorLine from "../../../components/SeparatorLine";
import type { SaveMode } from "../../../state/formVersion";

export const SETTINGS_SEARCH_FIELD_ID = "settings_search_fields";

const SettingsContent = () => {
  const { t } = useTranslation();
  const { fieldService, variableService } = FormServicesFactory.forRootForm();
  const { id: themeId } = FormSettingsService.getTheme();
  const options: SelectOption<SaveMode>[] = [
    {
      variant: "text",
      value: "SAVE_AND_CLOSE_ONLY",
      label: t(($) => $.FORM_SETTINGS_SEND_OPTIONS_OPTION_SEND_AND_CLOSE),
    },
    { variant: "text", value: "ALL", label: t(($) => $.FORM_SETTINGS_SEND_OPTIONS_OPTION_SEND_AND_NEW) },
    { variant: "text", value: "NO_SAVE", label: t(($) => $.FORM_SETTINGS_SEND_OPTIONS_OPTION_READ_ONLY) },
  ];

  return (
    <div class="flex flex-col gap-8">
      <ContentSection>
        <h2 class="text-header-xl text-primary">{t(($) => $.FORM_SETTINGS_GENERAL_SECTION_TITLE)}</h2>
        <SeparatorLine class="mt-2.5 mb-6" />

        <div class="flex flex-col gap-6">
          <ThemeSelect
            label={t(($) => $.FORM_SETTINGS_THEME_LABEL)}
            info={{
              title: t(($) => $.FORM_SETTINGS_THEME_LABEL),
              description: t(($) => $.FORM_SETTINGS_THEME_INFO_DESCRIPTION),
              alt: t(($) => $.FORM_SETTINGS_THEME_INFO_ARIA_LABEL),
            }}
            defaultValue={themeId}
            onChange={FormSettingsService.changeTheme}
          />

          <SingleSelectInput
            label={t(($) => $.FORM_SETTINGS_SEND_OPTIONS_LABEL)}
            info={{
              title: t(($) => $.FORM_SETTINGS_SEND_OPTIONS_LABEL),
              description: t(($) => $.FORM_SETTINGS_SEND_OPTIONS_INFO_DESCRIPTION),
              alt: t(($) => $.FORM_SETTINGS_SEND_OPTIONS_INFO_ARIA_LABEL),
            }}
            value={FormSettingsService.getSettings().saveMode}
            options={options}
            onSelect={(value) => {
              if (!isNil(value)) {
                FormSettingsService.changeSaveMode(value);
              }
            }}
            sameWidth
          />

          <MixedInput
            label={t(($) => $.FORM_SETTINGS_DESCRIPTION_IN_APP_LABEL)}
            info={{
              title: t(($) => $.FORM_SETTINGS_DESCRIPTION_IN_APP_LABEL),
              description: t(($) => $.FORM_SETTINGS_DESCRIPTION_IN_APP_INFO_DESCRIPTION),
              alt: t(($) => $.FORM_SETTINGS_DESCRIPTION_IN_APP_INFO_ARIA_LABEL),
            }}
            value={FormSettingsService.getSettings().descriptionInApp}
            parse={(value) =>
              parseStringTemplate(
                value,
                fieldService.getFieldsByTypes(SEARCHABLE_FIELDS),
                variableService.getVariablesByTypes(SEARCHABLE_VARIABLES),
              )
            }
            onChange={FormSettingsService.changeDescriptionInApp}
            options={createDynamicValueOptions(t, {
              fields: fieldService.getFieldsByTypes(SEARCHABLE_FIELDS).flatMap((field) => toFieldValueOptions(field)),
              subforms: [],
              customVariables: variableService.getVariablesByTypes(SEARCHABLE_VARIABLES).map(toVariableValueOption),
              metaVariables: [],
            })}
          />
        </div>
      </ContentSection>

      <ContentSection>
        <h2 class="mb-3 text-header-xl text-primary">{t(($) => $.FORM_SETTINGS_SEARCH_SECTION_TITLE)}</h2>

        <Toggle
          block={true}
          label={t(($) => $.FORM_SETTINGS_SEARCH_TOGGLE_LABEL)}
          info={{
            title: t(($) => $.FORM_SETTINGS_SEARCH_TOGGLE_LABEL),
            description: t(($) => $.FORM_SETTINGS_SEARCH_TOGGLE_INFO_DESCRIPTION),
            alt: t(($) => $.FORM_SETTINGS_SEARCH_TOGGLE_INFO_ARIA_LABEL),
          }}
          checked={FormSettingsService.getSettings().searchSettings.enabled}
          setChecked={FormSettingsService.enableSearch}
        />

        <Show when={FormSettingsService.getSettings().searchSettings.enabled}>
          <div class="flex flex-col gap-6">
            <SeparatorLine class="mt-2.5" />
            <Checkbox
              label={t(($) => $.FORM_SETTINGS_SEARCH_ALLOW_SEARCHING_ALL_LABEL)}
              info={{
                title: t(($) => $.FORM_SETTINGS_SEARCH_ALLOW_SEARCHING_ALL_LABEL),
                description: t(($) => $.FORM_SETTINGS_SEARCH_ALLOW_SEARCHING_ALL_INFO_DESCRIPTION),
                alt: t(($) => $.FORM_SETTINGS_SEARCH_ALLOW_SEARCHING_ALL_INFO_ARIA_LABEL),
              }}
              checked={FormSettingsService.getSettings().searchSettings.allowSearchingAllSubmissions}
              setChecked={(checked) => FormSettingsService.toggleAllowSearchingForAllSubmissions(checked === true)}
            />
            <MultiSelectWithSearchInput
              label={t(($) => $.FORM_SETTINGS_SEARCH_FIELDS_LABEL)}
              info={{
                title: t(($) => $.FORM_SETTINGS_SEARCH_FIELDS_LABEL),
                description: t(($) => $.FORM_SETTINGS_SEARCH_FIELDS_INFO_DESCRIPTION),
                alt: t(($) => $.FORM_SETTINGS_SEARCH_FIELDS_INFO_ARIA_LABEL),
              }}
              options={createDynamicValueOptions(t, {
                fields: fieldService.getFieldsByTypes(SEARCHABLE_FIELDS).flatMap(toFieldOption),
                subforms: [],
                customVariables: [],
                metaVariables: [],
              })}
              value={FormSettingsService.getSearchFields()}
              onSelect={(fieldIds) => {
                if (Array.isArray(fieldIds) && fieldIds.every((id) => id instanceof FieldId)) {
                  FormSettingsService.setSearchFields(fieldIds);
                  return;
                }
              }}
              width="matchTrigger"
            />
            <Toggle
              block={true}
              label={t(($) => $.FORM_SETTINGS_ENABLE_FILTERING_LABEL)}
              info={{
                title: t(($) => $.FORM_SETTINGS_ENABLE_FILTERING_LABEL),
                description: t(($) => $.FORM_SETTINGS_ENABLE_FILTERING_INFO_DESCRIPTION),
                alt: t(($) => $.FORM_SETTINGS_ENABLE_FILTERING_INFO_ARIA_LABEL),
              }}
              checked={FormSettingsService.getSettings().searchSettings.filteringEnabled}
              setChecked={FormSettingsService.enableFiltering}
            />
            <Show when={FormSettingsService.getSettings().searchSettings.filteringEnabled}>
              <FieldFilterSelection />
            </Show>
          </div>
        </Show>
      </ContentSection>
    </div>
  );
};

const SettingsContentWithErrorBoundary = withErrorBoundary(SettingsContent);

export default SettingsContentWithErrorBoundary;
